import * as PIXI from 'pixi.js'
import type { Viewport } from 'pixi-viewport'
import { debounce } from 'lodash'

interface Tile {
    tile: string
    x: number
    y: number
    size: number
    sold: boolean
    auctionable?: boolean
    rect?: PIXI.Graphics
}

interface TileMetaData {
    twitterHandle?: string
    twitterLink?: string
    ownerName?: string
    texture?: PIXI.Texture
}

export interface ActiveTile {
    src?: string
    ownerName?: string
    twitterHandle?: string
    twitterLink?: string
    size: number
    x: number
    y: number
    owned: boolean
    auctionable?: boolean
}

type ActiveTileCallback = (tile?: ActiveTile) => void
interface ImageData extends TileMetaData {
    src: string
    x: number
    y: number
}

type ConstructorType<T, TArgs> = new (...args: TArgs[]) => T
type ViewPortArgs = ConstructorParameters<typeof Viewport>[number]
type ViewPortClass = ConstructorType<Viewport, ViewPortArgs>
export class MapEngine {
    private tileActiveCallbacks = new Set<ActiveTileCallback>()
    private started: boolean = false
    private app!: PIXI.Application
    private tiles: Tile[] = []
    private miniMapApp!: PIXI.Application
    private viewport!: Viewport
    private miniMapViewport!: Viewport
    private miniMapHightLight!: PIXI.Graphics
    private activeTile: Tile | undefined
    private ownedTiles: [number, number][] = []
    private rects = new WeakMap()
    private innerWidth: number
    private innerHeight: number

    private tileColorMap: { [tile: string]: number } = {
        // ['nothing']: `rgb(0,0,0, 0)`,
        ['road']: 0x13323b,
        ['land1x1']: 0x48707b,
        ['land3x3']: 0x2b5460,
        ['land6x6']: 0x224650,
        ['land9x9']: 0x183943,
        ['land12x12']: 0x13323b,
    }

    private images: ImageData[] = [
        {
            src: 'drop.png',
            x: 161,
            y: 94,
            twitterHandle: '@DROPPgg',
            twitterLink: 'https://twitter.com/DROPPgg',
            ownerName: 'dropp',
        },
        {
            src: 'monkey.jpg',
            x: 103,
            y: 34,
            twitterHandle: '@SOLBigBrain',
            twitterLink: 'https://twitter.com/SOLBigBrain',
            ownerName: 'SOL Big Brain',
        },
        {
            src: 'dragonz.jpg',
            x: 120,
            y: 105,
            twitterHandle: 'https://boryokudragonz.io/',
            twitterLink: 'https://boryokudragonz.io/',
            ownerName: 'Boryoku DragonZ',
        },
        {
            src: 'dragonz2.jpg',
            x: 108,
            y: 120,
        },
        {
            src: 'animoca.png',
            x: 89,
            y: 32,
            twitterHandle: 'https://www.animocabrands.com/',
            twitterLink: 'https://www.animocabrands.com/',
            ownerName: 'Animoca Brands',
        },
        {
            src: 'zombie.png',
            x: 147,
            y: 113,
            twitterHandle: '@lilbaby4PF',
            twitterLink: 'https://twitter.com/lilbaby4PF',
            ownerName: 'Lil Baby',
        },
        {
            src: 'solice.png',
            x: 147,
            y: 91,
            twitterHandle: '@solice_io',
            twitterLink: 'https://twitter.com/solice_io',
            ownerName: 'Solice',
        },
        {
            src: 'skull.png',
            x: 137,
            y: 105,
            twitterHandle: '@solBOOGLE',
            twitterLink: 'https://twitter.com/solBOOGLE',
            ownerName: 'BOOGLE',
        },
        {
            src: 'genblocks.png',
            x: 112,
            y: 34,
            twitterHandle: 'https://genblock.capital/',
            twitterLink: 'https://genblock.capital/',
            ownerName: 'Genblock Capital',
        },
        {
            src: 'happymonkey.jpg',
            x: 120,
            y: 122,
            twitterHandle: 'https://zillaz.net/',
            twitterLink: 'https://zillaz.net/',
            ownerName: 'Zillaz',
        },
        {
            src: 'skull2.jpg',
            x: 120,
            y: 115,
            twitterHandle: '@earlyishadopter',
            twitterLink: 'https://twitter.com/earlyishadopter',
            ownerName: 'j◎rds.sol //',
        },
        {
            src: 'skullking.jpg',
            x: 111,
            y: 169,
            twitterLink: 'https://twitter.com/softmoneyinc',
            twitterHandle: '@softmoneyinc',
            ownerName: 'SoftMoney',
        },
        {
            src: 'cooldragon.jpg',
            x: 94,
            y: 157,
        },
        {
            src: 'noskull.jpg',
            x: 117,
            y: 188,
        },
        {
            src: 'skullpharao.jpg',
            x: 123,
            y: 176,
            twitterHandle: '@rainnen23',
            twitterLink: 'https://twitter.com/rainnen23',
            ownerName: 'Rain',
        },
        {
            src: 'skulldiamonds.jpg',
            x: 85,
            y: 167,
        },
        {
            src: 'rlosc.jpg',
            x: 42,
            y: 77,
            twitterHandle: 'https://redlionsportsclub.xyz/',
            twitterLink: 'https://redlionsportsclub.xyz/',
            ownerName: 'Red lion sports club',
        },
    ]

    public constructor(private element: HTMLDivElement, private miniMapElement: HTMLDivElement) {
        this.innerHeight = window.innerHeight
        this.innerWidth = window.innerWidth
    }

    private renderMainMap(Viewport: ViewPortClass, element: HTMLDivElement) {
        this.app = new PIXI.Application({ backgroundAlpha: 0 })
        this.app.renderer.resize(window.innerWidth, window.innerHeight)
        this.viewport = new Viewport({
            screenWidth: window.innerWidth,
            screenHeight: window.innerHeight,
            worldWidth: 256,
            worldHeight: 256,
            passiveWheel: false,
            interaction: this.app.renderer.plugins.interaction, // the interaction module is important for wheel to work properly when renderer.view is placed or scaled
        })
        element.appendChild(this.app.view)
        this.app.stage.addChild(this.viewport)
        this.viewport.drag().pinch().wheel().decelerate()
    }

    public renderOwnedTiles(tilePositions: [number, number][]) {
        this.ownedTiles = tilePositions
        this.redrawTiles()
    }

    public setActiveTile(x: number, y: number) {
        if (!this.started) return

        const tile = this.tiles.find((tile) => tile.x === x && tile.y === y)
        this.activeTile = tile
        if (tile) this.onTileClick(tile)
    }

    private renderMiniMap(Viewport: ViewPortClass, element: HTMLDivElement) {
        this.miniMapApp = new PIXI.Application({
            backgroundAlpha: 0.25,
            backgroundColor: 0x000000,
        })
        this.miniMapApp.renderer.resize(256, 256)
        this.miniMapViewport = new Viewport({
            screenWidth: 256,
            screenHeight: 256,
            worldWidth: 256,
            worldHeight: 256,
            passiveWheel: false,
            interaction: this.miniMapApp.renderer.plugins.interaction, // the interaction module is important for wheel to work properly when renderer.view is placed or scaled
        })
        element.appendChild(this.miniMapApp.view)
        this.miniMapApp.stage.addChild(this.miniMapViewport)
    }

    public async start(tiles: Tile[]) {
        this.tiles = tiles
        // Dynamic import viewport in order to workaround nextjs's ssr
        const Viewport = (await import('pixi-viewport')).Viewport

        this.renderMainMap(Viewport, this.element)

        this.renderMiniMap(Viewport, this.miniMapElement)
        this.drawMiniMap()

        this.setupListeners()

        this.draw()
        this.drawImages()
        this.resetZoom()
        this.started = true
    }

    public stop() {
        this.removeListeners()
        this.tileActiveCallbacks.clear()
        this.viewport.removeAllListeners()
        this.miniMapViewport.removeAllListeners()
        this.miniMapViewport.destroy({
            children: true,
            texture: true,
            baseTexture: true,
        })
        this.miniMapApp.destroy(true)

        this.viewport.destroy({
            children: true,
            texture: true,
            baseTexture: true,
        })
        this.app.destroy(true)

        this.tiles.forEach((tile) => this.rects.delete(tile))
        this.tiles = []
        this.started = false
    }

    public onActiveTile(cb: ActiveTileCallback) {
        this.tileActiveCallbacks.add(cb)
    }

    public zoomIn() {
        this.viewport.zoomPercent(0.15, true)
        this.setMiniMapHighlightPostion(this.viewport.transform.scale, this.viewport.transform.position)
    }

    public zoomOut() {
        this.viewport.zoomPercent(-0.15, true)
        this.setMiniMapHighlightPostion(this.viewport.transform.scale, this.viewport.transform.position)
    }

    public clearActiveTile() {
        this.activeTile = undefined
        this.tileActiveCallbacks.forEach((cb) => {
            cb(undefined)
        })
        this.redrawTiles()
    }

    public resetZoom() {
        this.viewport.fitWorld()
        this.viewport.moveCenter(256 / 2, 256 / 2 - 35)
        this.setMiniMapHighlightPostion(this.viewport.transform.scale, this.viewport.transform.position)
    }

    private setupListeners() {
        window.addEventListener('resize', this.resize)
        this.viewport.on('moved', (e) => {
            const position = e.viewport.transform.position
            const scale = e.viewport.transform.scale
            this.setMiniMapHighlightPostion(scale, position)
        })
    }

    private removeListeners() {
        window.removeEventListener('resize', this.resize)
    }

    private resize = debounce(() => {
        this.innerWidth = window.innerWidth
        this.innerHeight = window.innerHeight

        this.setMiniMapHighlightPostion(this.viewport.transform.scale, this.viewport.transform.position)

        this.app.renderer.resize(window.innerWidth, window.innerHeight)
        this.viewport.resize(window.innerWidth, window.innerHeight)
    }, 200)

    private draw() {
        for (const tile of this.tiles) {
            if (!this.rects.get(tile)) {
                this.rects.set(
                    tile,
                    new PIXI.Graphics()
                        .beginFill(tile === this.activeTile ? 0x00c8ff : this.tileColorMap[tile.tile])
                        .drawRect(tile.x, tile.y, tile.size, tile.size)
                )
                this.rects.get(tile).interactive = true
                this.viewport.addChild(this.rects.get(tile))
            }

            this.rects.get(tile).on('pointerdown', () => {
                this.onTileClick(tile)
            })
        }
    }

    private drawMiniMap() {
        for (const tile of this.tiles) {
            if (!this.rects.get(tile)) {
                const rect = new PIXI.Graphics()
                    .beginFill(this.tileColorMap[tile.tile])
                    .drawRect(tile.x, tile.y, tile.size, tile.size)
                this.miniMapViewport.addChild(rect)
            }
        }

        if (!this.miniMapHightLight) {
            this.miniMapHightLight = new PIXI.Graphics()
                .beginFill(0xffffff, 0.05)
                .lineStyle(1, 0x467c8a, 0.75, 0.5, true)
                .drawRoundedRect(0, 0, 256, 256, 8)
            this.miniMapViewport.addChild(this.miniMapHightLight)
        }
    }

    private setMiniMapHighlightPostion(
        scale: {
            x: number
            y: number
        },
        position: {
            x: number
            y: number
        }
    ) {
        const width = scale.x * 256
        const height = scale.y * 256

        // Figure out what percentage the map is 'outside the browser's window'
        const positionX1 = Math.min(1, Math.max(0, -(position.x / width)))
        const positionX2 = Math.min(1, Math.max(0, position.x + width - this.innerWidth) / width)
        const positionY1 = Math.min(1, Math.max(0, -(position.y / height)))
        const positionY2 = Math.min(1, Math.max(0, position.y + height - this.innerHeight) / height)

        // we need to offset the X1 and Y1 from the total width of the container
        // so the size is correct.
        const offsetX = 256 * positionX1
        const offsetY = 256 * positionY1

        // Math.max so the min is never 0 (avoids the border not being draw)
        this.miniMapHightLight.x = Math.max(1, 256 * positionX1)
        this.miniMapHightLight.y = Math.max(0, 256 * positionY1)
        // max 255 so the max is never 256 (avoids the border not being draw)
        this.miniMapHightLight.width = 255 - 256 * positionX2 - offsetX
        this.miniMapHightLight.height = 256 - 256 * positionY2 - offsetY
    }

    private onTileClick(tile: Tile) {
        if (tile.tile !== 'road') {
            const imageData = this.images.find((image) => image.x == tile.x && tile.y === image.y)
            this.activeTile = tile
            this.tileActiveCallbacks.forEach((cb) =>
                cb({
                    src: imageData?.src,
                    ownerName: imageData?.ownerName,
                    twitterHandle: imageData?.twitterHandle,
                    twitterLink: imageData?.twitterLink,
                    size: tile.size,
                    x: tile.x,
                    y: tile.y,
                    owned: this.isTileOwned(tile),
                    auctionable: tile.auctionable,
                })
            )
            this.redrawTiles()
        }
    }

    private redrawTiles() {
        // redraw the tiles
        for (const tile of this.tiles) {
            // todo (optimization): only redraw the previous selected and current selected tile.
            this.rects
                .get(tile)
                .clear()
                .beginFill(this.getTileFill(tile))
                .drawRect(tile.x, tile.y, tile.size, tile.size)
        }
    }

    private getTileFill(tile: Tile) {
        if (tile === this.activeTile) {
            return 0x00c8ff
        }

        if (this.isTileOwned(tile)) {
            return 0xff00dc
        }

        if (tile.auctionable) {
            return 0x26bb81
        }

        return this.tileColorMap[tile.tile]
    }

    private isTileOwned(tile: Tile) {
        return !!this.ownedTiles.find(([x, y]) => tile.x === x && tile.y === y)
    }

    private drawImages() {
        for (const img of this.images) {
            const tile = this.tiles.find((tile) => tile.x === img.x && tile.y === img.y)
            if (tile) {
                PIXI.Texture.fromURL(`/${img.src}`).then((texture) => {
                    const sprite = new PIXI.Sprite(texture)
                    sprite.x = tile.x
                    sprite.y = tile.y
                    sprite.width = tile.size
                    sprite.height = tile.size
                    this.viewport.addChild(sprite)
                })
            }
        }
    }
}
